import React, { useEffect, useRef, useState, useMemo } from 'react';
import { ConfigProvider, Modal } from 'antd';
import ptBR from 'antd/es/locale/pt_BR';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Spinner } from '@styles/loading.js';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import ButtonComp from '@components/UI/Button/Index';
import { WarningOptionComp } from '@components/UI/WarningOption/Index.jsx';
import SweetAlert from '@components/Feedback/SweetAlert/Index';
import { SaveDocumentoSharepoint, DeleteDocumento } from '@services/Documento/Index';
import { readFileAsBase64 } from './ReadFile';
import { allowedExtensions } from './FileExtensions';
import { carregarDocumentos } from '@store/slices/documentosSlice';
import { makeSelectDocumentosByItemId } from '@store/selectors/documentosSelector';
import { useAppAuth } from '@contexts/AuthContext';
import { useDisableIfStatusAndRole } from '@components/UI/DisableIfStatusAndRole/Index';
import { perfisAdmin, perfisUnidadeGerencia, perfisVisitante } from '@utils/auth/perfis';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

export default function UploadFileModal({ visible, idSolicitacaoProcessoItem, idSolicitacao, closeModal }) {
  const dispatch = useDispatch();
  const { nome } = useAppAuth();

  // Selector for documentos
  const documentosState = useSelector(
    useMemo(() => makeSelectDocumentosByItemId(idSolicitacaoProcessoItem), [idSolicitacaoProcessoItem])
  );

  const { role } = useAppAuth();

  const fileInputRef = useRef(null);
  const [localFiles, setLocalFiles] = useState({});

  const [tableLoading, setTableLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // REGRAS DELEÇÃO DE DOCS - status em que será desabilitado a exclusão dos docs
  const hideForAdmin = useDisableIfStatusAndRole(idSolicitacao, [2, 3, 5, 7], perfisAdmin);
  const hideForUnidadeGerencia = useDisableIfStatusAndRole(idSolicitacao, [4, 6, 7], perfisUnidadeGerencia);

  // REGRAS ADIÇÃO DE DOCS - status em que será habilitado a adição de novos docs
  const allowAddFilesAdmin = useDisableIfStatusAndRole(idSolicitacao, [1, 4, 6], perfisAdmin);
  const allowAddFilesUnidadeGerencia = useDisableIfStatusAndRole(idSolicitacao, [3, 5], perfisUnidadeGerencia);
  const hideAddFile = allowAddFilesAdmin || allowAddFilesUnidadeGerencia;

  const isVisitante = perfisVisitante.includes(role);

  // Reset local cache when modal closes
  useEffect(() => {
    if (!visible) setLocalFiles({});
  }, [visible]);

  /** Open a document link */
  const openDocument = (file) => {
    if (file.urlDocumentoSharepoint) {
      window.open(file.urlDocumentoSharepoint, '_blank');
    }
    else {
      SweetAlert.toastError('Salve o documento antes de visualizá-lo.');
    }
  };

  /** Handle adding new files */
  const handleAddFiles = async (e) => {
    setActionLoading(true);
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      setActionLoading(false);
      return;
    }

    const updated = [...documentosState];
    const newLocals = { ...localFiles };

    for (let file of files) {
      if (file.size > MAX_FILE_SIZE) {
        SweetAlert.toastError(`Arquivo muito grande: \"${file.name}\"`);
        continue;
      }
      if (!allowedExtensions.includes(file.type)) {
        SweetAlert.toastError(`Extensão não permitida: \"${file.name}\".`);
        continue;
      }
      if (updated.some(d => d.nomeDocumento === file.name)) {
        SweetAlert.toastInfo(`Arquivo \"${file.name}\" já adicionado.`);
        continue;
      }

      const [, base64] = (await readFileAsBase64(file)).split(',');
      const tempId = uuidv4();
      updated.push({
        tempId,
        nomeDocumento: file.name,
        tamanhoArquivoEmMB: +(file.size / 1024 / 1024).toFixed(4),
        dataUpload: new Date().toISOString(),
        nomeUsuario: nome || '-',
        isNew: true,
      });
      newLocals[tempId] = { file, base64 };
    }

    dispatch(carregarDocumentos({ idProcessoItem: idSolicitacaoProcessoItem, documentos: updated }));
    setLocalFiles(newLocals);
    e.target.value = '';
    setActionLoading(false);
  };

  /** Handle deletion of a file */
  const handleDeleteFile = async (file) => {
    const ok = await SweetAlert.delete(
      `Esta ação excluirá: <strong>${file.nomeDocumento}</strong>`
    );
    if (!ok) return;

    setActionLoading(true);
    if (file.id) {
      SweetAlert.toastLoading();
      await DeleteDocumento(file.id);
    }

    const remaining = documentosState.filter(
      d => (file.id ? d.id !== file.id : d.tempId !== file.tempId)
    );
    const { [file.tempId]: _, ...rest } = localFiles;

    dispatch(carregarDocumentos({ idProcessoItem: idSolicitacaoProcessoItem, documentos: remaining }));
    setLocalFiles(rest);

    SweetAlert.toastSuccess('Documento excluído com sucesso!');

    setActionLoading(false);
  };

  /** Clear unsaved new files */
  const handleClearFiles = () => {
    const onlyExisting = documentosState.filter(d => !d.isNew);

    if (onlyExisting.length === documentosState.length) {
      SweetAlert.toastInfo('Nenhum arquivo novo carregado.');
    }

    dispatch(carregarDocumentos({ idProcessoItem: idSolicitacaoProcessoItem, documentos: onlyExisting }));
    setLocalFiles({});
  };

  /** Upload new files to SharePoint */
  const sendNewFilesToAPI = async () => {
    const newDocs = documentosState.filter(d => d.isNew);
    let updated = [...documentosState];
    let error = false;

    for (let doc of newDocs) {
      const data = localFiles[doc.tempId];
      if (!data) continue;

      const form = new FormData();
      form.append('IdSolicitacaoProcessoItem', idSolicitacaoProcessoItem);
      form.append('Arquivo', data.file);

      try {
        const saved = await SaveDocumentoSharepoint(form);
        updated = updated.map(d => d.tempId === doc.tempId ? { ...saved, isNew: false } : d);
        delete localFiles[doc.tempId];
      } catch {
        error = true;
        const errorMessage = error.parsed?.message || 'Não foi possível salvar o arquivo.';

        SweetAlert.errorHTML('Erro ao cadastrar', errorMessage);
      }
    }

    dispatch(carregarDocumentos({ idProcessoItem: idSolicitacaoProcessoItem, documentos: updated }));
    return !error;
  };

  /** Confirm and submit files */
  const handleSubmit = async () => {
    if (!documentosState.some(d => d.isNew)) {
      SweetAlert.toastInfo('Nenhum arquivo novo para salvar.');
      return;
    }
    setActionLoading(true);
    SweetAlert.toastLoading();

    const success = await sendNewFilesToAPI();

    if (success) {
      SweetAlert.toastSuccess('Arquivos salvos com sucesso!');
    } else {
      SweetAlert.toastError('Alguns documentos falharam.');
    }

    setActionLoading(false);
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, i) => i + 1,
      width: 100
    },
    {
      title: 'NOME DO ARQUIVO',
      dataIndex: 'nomeDocumento',
      key: 'nomeDocumento',
      width: 200,
      render: (text, record) => {
        const saved = record.id || record.urlDocumentoSharepoint;
        return (
          <span
            style={{ textDecoration: saved ? 'underline' : 'none', cursor: saved ? 'pointer' : 'not-allowed', color: saved ? 'inherit' : 'gray' }}
            onClick={() => saved ? openDocument(record) : SweetAlert.toastError('Salve o documento antes de visualizá-lo.')}
          >
            {text}
          </span>
        );
      }
    },
    {
      title: 'DATA DE UPLOAD',
      dataIndex: 'dataUpload',
      key: 'dataUpload',
      width: 200,
      render: date => new Date(date).toLocaleString('pt-BR')
    },
    {
      title: 'NOME USUÁRIO',
      dataIndex: 'nomeUsuario',
      key: 'nomeUsuario',
      width: 200
    },
    {
      title: 'TAMANHO',
      dataIndex: 'tamanhoArquivoEmMB',
      key: 'tamanhoArquivoEmMB',
      width: 150,
      render: size => `${size.toFixed(4)} MB`
    },
    {
      title: 'AÇÕES',
      key: 'actions',
      width: 100,
      render: (record) => {
        const isUploader = record.nomeUsuario === nome;
        const canDeleteNew = record.isNew && isUploader;
        const canDeleteSaved =
          !record.isNew &&
          isUploader &&
          !hideForAdmin &&
          !hideForUnidadeGerencia &&
          !isVisitante;
        const canDelete = canDeleteNew || canDeleteSaved;

        return (
          <div className="d-flex justify-content-center gap-3">
            {record.isNew && <WarningOptionComp />}
            <i
              className={
                canDelete
                  ? 'fa-solid fa-trash fs-6 text-danger'
                  : 'fa-solid fa-lock fs-6 text-muted'
              }
              style={{ cursor: canDelete ? 'pointer' : 'not-allowed' }}
              onClick={() =>
                canDelete
                  ? handleDeleteFile(record)
                  : SweetAlert.toastInfo('Ação indisponível para este documento.')
              }
              title={canDelete ? 'Excluir' : 'Ação indisponível'}
            />
          </div>
        );
      }
    }
  ];

  const titleForUpload = "Upload de Documentos";
  const titleForView = "Visualização de Documentos";

  return (
    <div className="d-flex justify-content-center gap-2">
      <Modal
        getContainer={false}
        open={visible}
        onCancel={closeModal}
        width={1000}
        centered
        footer={null}
        title={hideAddFile && (
          titleForUpload
        ) || titleForView}>

        {(hideAddFile) && (
          <div className="text-center">

            <p className="fs-6 mb-1"><strong>Formatos permitidos:</strong> Word, Excel, PowerPoint, PDF, ZIP.</p>

            <p className="fs-6 mb-1"><strong>Tamanho máximo:</strong> 50MB por arquivo.</p>

            <p className="fs-6 mb-1">
              <WarningOptionComp /> Este ícone, quando exibido, indica que o documento ainda não foi salvo.
            </p>

            <div className="d-flex justify-content-center gap-2 my-3">
              <ButtonComp
                title="ADICIONAR ARQUIVO"
                onClick={() => fileInputRef.current?.click()}
                buttonColor="#2b97be"
                classnameIcon="fa-solid fa-upload"
                hoverMessage="Adicionar arquivo"
                type="button"
                loading={actionLoading}
              />
              <ButtonComp
                title="ATUALIZAR"
                onClick={handleClearFiles}
                buttonColor="#125578"
                classnameIcon="fa-solid fa-rotate"
                hoverMessage="Limpar arquivos não salvos"
                type="button"
                disabled={actionLoading}
              />
            </div>
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept={allowedExtensions.join(',')}
          multiple
          onChange={handleAddFiles}
          disabled={actionLoading}
        />

        <div className='mt-4'>
          <ConfigProvider locale={ptBR}>
            <StyledTable
              columns={columns}
              dataSource={documentosState}
              rowKey={record => record.id || record.tempId}
              loading={{ spinning: tableLoading, indicator: <Spinner /> }}
              pagination={{ pageSize: 5, showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros` }}
              scroll={{ y: 300, x: 'max-content' }}
              locale={{ emptyText: <h3 className='m-3'>Nenhum documento disponível.</h3> }}
            />
          </ConfigProvider>
        </div>

        {(hideAddFile) && (
          <div className="d-flex justify-content-end gap-2 mt-4">
            <ButtonComp
              title="ANEXAR"
              onClick={handleSubmit}
              buttonColor="#2b97be"
              classnameIcon="fa-solid fa-floppy-disk"
              hoverMessage="Salvar alterações"
              type="button"
              loading={actionLoading}
            />
          </div>
        )}

      </Modal>
    </div >
  );
}